import { RoleTag } from './RoleTag';

// Decision level priority order (lower index = higher priority)
const DECISION_LEVEL_PRIORITY: Record<string, number> = {
  'executive': 0,
  'functional leader': 1,
  'school leadership': 2,
  'operational support': 3,
  'practitioner': 4,
};

// Sort roles by decision level priority first, then by rank as tiebreaker
function sortRolesByPriority(roles: any[]): any[] {
  return [...roles].sort((a, b) => {
    const aPriority = a.decisionLevel ? (DECISION_LEVEL_PRIORITY[a.decisionLevel.toLowerCase()] ?? 999) : 999;
    const bPriority = b.decisionLevel ? (DECISION_LEVEL_PRIORITY[b.decisionLevel.toLowerCase()] ?? 999) : 999;

    // Primary sort: decision level priority (lower = better)
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // Tiebreaker: rank (higher = better)
    if (a.rank == null && b.rank == null) return 0;
    if (a.rank == null) return 1;
    if (b.rank == null) return -1;
    return b.rank - a.rank;
  });
}

interface RoleSummary {
  decisionLevel: string | null;
  functionalGroups: string[];
  remainingRoleCount: number;
}

/**
 * Get tags to display for a contact's roles.
 * Shows the highest decision level and all unique functional groups at that level.
 */
function getRoleSummary(roles: any[]): RoleSummary {
  if (!roles || roles.length === 0) {
    return { decisionLevel: null, functionalGroups: [], remainingRoleCount: 0 };
  }

  const sortedRoles = sortRolesByPriority(roles);
  const highestRole = sortedRoles[0];
  const highestDecisionLevel = highestRole?.decisionLevel?.toLowerCase() || null;

  // Get all unique functional groups from roles at the same decision level
  const functionalGroups: string[] = [];
  let rolesAtHighestLevel = 0;

  for (const role of sortedRoles) {
    const roleDecisionLevel = role.decisionLevel?.toLowerCase() || null;
    if (roleDecisionLevel === highestDecisionLevel) {
      rolesAtHighestLevel++;
      if (role.functionalGroup && !functionalGroups.includes(role.functionalGroup)) {
        functionalGroups.push(role.functionalGroup);
      }
    }
  }

  // Remaining roles are those not at the highest decision level
  const remainingRoleCount = roles.length - rolesAtHighestLevel;

  return {
    decisionLevel: highestRole?.decisionLevel || null,
    functionalGroups,
    remainingRoleCount
  };
}

interface RoleTagsSummaryProps {
  roles: any[];
  size?: 'small' | 'normal';
  bg?: 'hard' | 'medium';
  showRoleCount?: boolean;
}

export function RoleTagsSummary({ roles, size = 'small', bg = 'medium', showRoleCount = true }: RoleTagsSummaryProps) {
  const summary = getRoleSummary(roles);

  return (
    <span style={{ marginLeft: -3, display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {summary.decisionLevel && (
        <RoleTag type="decisionLevel" value={summary.decisionLevel} size={size} bg={bg} />
      )}
      {summary.functionalGroups.map((fg) => (
        <RoleTag key={fg} type="functionalGroup" value={fg} size={size} bg={bg} />
      ))}
      {showRoleCount && summary.remainingRoleCount > 0 && (
        <span className={`role-tag role-tag--${size} role-tag--bg-${bg} role-tag--gray`}>
          +{summary.remainingRoleCount} role{summary.remainingRoleCount > 1 ? 's' : ''}
        </span>
      )}
    </span>
  );
}

// Export utility functions for use elsewhere
export { sortRolesByPriority };
